/**
 * Agricultural GHG for county inventory (MT CO₂e), aligned with legacy `agriculture_filtered`.
 *
 * Priority:
 * 1. Table/view **`agriculture_filtered`** (`geofips`, `year`, `emissions` in MT) — same basis as MySQL GHG backend.
 * 2. Fallback: sum all **`ag_emissions`** category rows × AG_EMISSIONS_DB_TO_MT per year (can diverge if view excludes overlaps).
 *
 * Policy: allocate to **counties** only; **cities** get 0 (no double-counting with county inventory).
 */
import type { SupabaseClient } from '@supabase/supabase-js';

/** Same scaling as AgricultureChart.tsx when reading raw `ag_emissions` cells */
export const AG_EMISSIONS_DB_TO_MT = 1_000_000;

/** Years with ag_emissions / filtered columns in current pipelines */
export const AG_EMISSIONS_YEAR_MIN = 2005;
export const AG_EMISSIONS_YEAR_MAX = 2023;

export function shouldIncludeAgricultureInCommunityInventory(
  geographyType: string | null | undefined
): boolean {
  return (geographyType || '').trim() === 'County';
}

function emptyByYear(): Record<number, number> {
  const byYear: Record<number, number> = {};
  for (let y = AG_EMISSIONS_YEAR_MIN; y <= AG_EMISSIONS_YEAR_MAX; y++) {
    byYear[y] = 0;
  }
  return byYear;
}

function isMissingRelationError(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes('does not exist') ||
    m.includes('schema cache') ||
    m.includes('not find the table') ||
    m.includes('unknown relation')
  );
}

/**
 * Read pre-aggregated county × year totals (MT CO₂e) from `agriculture_filtered`.
 * Returns null if the relation is missing or no rows match any geofips variant.
 */
async function tryFetchAgricultureFiltered(
  client: SupabaseClient,
  countyGeofips: string
): Promise<Record<number, number> | null> {
  const trimmed = String(countyGeofips).trim();
  const attempts: Array<string | number> = [trimmed];
  if (/^\d+$/.test(trimmed)) {
    const n = Number(trimmed);
    attempts.push(n);
    if (n > 0 && n < 100000) {
      attempts.push(String(n).padStart(4, '0'));
      attempts.push(String(n).padStart(5, '0'));
    }
  }

  const uniq = Array.from(new Set(attempts.map((a) => (typeof a === 'number' ? a : String(a)))));

  for (const g of uniq) {
    const q = await client
      .from('agriculture_filtered')
      .select('year, emissions')
      .eq('geofips', g as string & number)
      .gte('year', AG_EMISSIONS_YEAR_MIN)
      .lte('year', AG_EMISSIONS_YEAR_MAX)
      .order('year', { ascending: true });

    if (q.error) {
      if (isMissingRelationError(q.error.message)) {
        return null;
      }
      console.warn('agriculture_filtered query failed:', q.error.message);
      continue;
    }

    const rows = q.data ?? [];
    if (rows.length === 0) {
      continue;
    }

    const byYear = emptyByYear();
    for (const row of rows as Record<string, unknown>[]) {
      const y = Number(row.year);
      const raw = row.emissions;
      const e = typeof raw === 'number' ? raw : Number(raw);
      if (Number.isFinite(y) && y >= AG_EMISSIONS_YEAR_MIN && y <= AG_EMISSIONS_YEAR_MAX && Number.isFinite(e)) {
        byYear[y] = Math.round(e);
      }
    }

    return byYear;
  }

  return null;
}

/** Match `sql/create_agriculture_filtered_from_ag_emissions.sql`: avoid total+components double-count. */
const AGRICULTURE_TOTAL_LABEL_RATIO = 1.75;

function isAgricultureSectorTotalRow(row: Record<string, unknown>): boolean {
  const v2 = String(row.var_name2 ?? '')
    .trim()
    .toLowerCase();
  return v2 === 'agriculture';
}

/**
 * Sum `ag_emissions` for a county FIPS (MT CO₂e). If `var_name2 = 'agriculture'` exists and blind sum ≥ 1.75×
 * that row, use the labeled total only (same as `agriculture_filtered` view).
 */
async function fetchAgricultureFromAgEmissionsPivot(
  client: SupabaseClient,
  countyGeofips: string
): Promise<Record<number, number>> {
  const byYear = emptyByYear();
  const sumAllRaw: Record<number, number> = {};
  const sumLabelRaw: Record<number, number> = {};
  for (let y = AG_EMISSIONS_YEAR_MIN; y <= AG_EMISSIONS_YEAR_MAX; y++) {
    sumAllRaw[y] = 0;
    sumLabelRaw[y] = 0;
  }

  let { data, error } = await client.from('ag_emissions').select('*').eq('geofips', countyGeofips);

  if (error) {
    console.warn('agricultureEmissionsInventory: ag_emissions query failed:', error.message);
    return byYear;
  }

  if (!data?.length && /^\d+$/.test(String(countyGeofips).trim())) {
    const n = Number(countyGeofips);
    const r2 = await client.from('ag_emissions').select('*').eq('geofips', n);
    if (!r2.error && r2.data?.length) {
      data = r2.data;
    }
  }

  if (!data?.length) {
    return byYear;
  }

  for (const row of data as Record<string, unknown>[]) {
    const label = isAgricultureSectorTotalRow(row);
    for (let y = AG_EMISSIONS_YEAR_MIN; y <= AG_EMISSIONS_YEAR_MAX; y++) {
      const key = String(y);
      if (!(key in row)) {
        continue;
      }
      const raw = row[key];
      const v = typeof raw === 'number' ? raw : Number(raw);
      if (!Number.isFinite(v)) continue;
      sumAllRaw[y] += v;
      if (label) {
        sumLabelRaw[y] += v;
      }
    }
  }

  for (let y = AG_EMISSIONS_YEAR_MIN; y <= AG_EMISSIONS_YEAR_MAX; y++) {
    const all = sumAllRaw[y];
    const lab = sumLabelRaw[y];
    const rawPick =
      lab > 0 && all >= lab * AGRICULTURE_TOTAL_LABEL_RATIO ? lab : all;
    byYear[y] = Math.round(rawPick * AG_EMISSIONS_DB_TO_MT);
  }
  return byYear;
}

/**
 * County agricultural totals (MT CO₂e) by year — prefers `agriculture_filtered`, else `ag_emissions` pivot sum.
 */
export async function fetchAgricultureEmissionsByYearMt(
  client: SupabaseClient,
  countyGeofips: string | null | undefined,
  geographyType: string | null | undefined
): Promise<Record<number, number>> {
  const empty = emptyByYear();

  if (!countyGeofips || !shouldIncludeAgricultureInCommunityInventory(geographyType)) {
    return empty;
  }

  const cg = String(countyGeofips).trim();
  const fromFiltered = await tryFetchAgricultureFiltered(client, cg);
  if (fromFiltered !== null) {
    return fromFiltered;
  }

  return fetchAgricultureFromAgEmissionsPivot(client, cg);
}

/** Single year total (MT). */
export async function fetchAgricultureEmissionsMtForYear(
  client: SupabaseClient,
  countyGeofips: string | null | undefined,
  geographyType: string | null | undefined,
  year: number
): Promise<number> {
  const m = await fetchAgricultureEmissionsByYearMt(client, countyGeofips, geographyType);
  return m[year] ?? 0;
}
